import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { Typography } from '../Typography/Typography';
import Button from '../Button/Button';
import { Close } from '../../assets/icons/Close';
import styles from './Prompt.module.scss';
import classNames from 'classnames/bind';
import { CSSTransition } from 'react-transition-group';

const cx = classNames.bind(styles);

export interface PromptProps {
  children: ReactNode;
  closePrompt: () => void;
  open: boolean;
  buttonHandler?: () => void;
  buttonText?: string;
  promptHeading: string;
  size?: 'small' | 'medium' | 'large';
  backButton?: boolean;
  buttonType?: 'button' | 'submit';
  isSubmitting?: boolean;
  backButtonText?: string;
  className?: string;
}

const Prompt = ({
  children,
  closePrompt,
  open,
  buttonHandler,
  buttonText,
  promptHeading,
  backButton = false,
  size = 'medium',
  buttonType = 'button',
  isSubmitting,
  backButtonText = 'Back',
  className,
}: PromptProps): ReactElement => {
  const ref = useRef(null);
  useEffect(() => {
    if (open) {
      document.body.classList.add(styles['no-scroll']);
    } else {
      document.body.classList.remove(styles['no-scroll']);
    }
    return () => document.body.classList.remove(styles['no-scroll']);
  }, [open]);

  return (
    <>
      <CSSTransition
        nodeRef={ref}
        in={open}
        timeout={200}
        unmountOnExit
        classNames={{
          enter: cx('animation-enter'),
          enterActive: cx('animation-enter-active'),
          exit: cx('animation-exit'),
          exitActive: cx('animation-exit-active'),
        }}
      >
        <div className={cx('prompt-backdrop')} ref={ref}>
          <div
            className={`${cx('prompt', {
              'prompt-small': size === 'small',
              'prompt-medium': size === 'medium',
              'prompt-large': size === 'large',
            })} ${className}`}
          >
            <button type="button" aria-label="close prompt" className={styles['close-icon']} onClick={closePrompt}>
              <Close />
            </button>
            <div className={styles['prompt-content']}>
              <Typography
                className={styles['prompt-heading']}
                size={size === 'small' ? 'XL' : 'XXL'}
                weight="semi-bold"
              >
                {promptHeading}
              </Typography>
              {children}
            </div>
            <div className={styles['button-container']}>
              {backButton && (
                <Button size="full-width" onClick={closePrompt} primary={!buttonHandler}>
                  {backButtonText}
                </Button>
              )}
              {buttonHandler && (
                <Button loading={isSubmitting} type={buttonType} size="full-width" primary onClick={buttonHandler}>
                  {buttonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  );
};

export default Prompt;
